import { Dropdown, Input, Option } from "@fluentui/react-components"
import { type ProblemDetails, USERNAME_REGEX } from "@rema/shared"
import type { DomainPage } from "@rema/shared/schemas/domains"
import type {
  AdminUser,
  CreateUserRequest,
  UpdateUserRequest,
  UserStatus,
} from "@rema/shared/schemas/users"
import { useQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { useForm } from "react-hook-form"
import { AppDrawerForm } from "../../components/app-drawer-form"
import { FormField } from "../../components/form-field"
import { ApiError, api } from "../../lib/api"
import { applyFieldErrors, problemMessage } from "../../lib/problem"
import { usersI18n as i18n } from "./i18n"

interface UserFormValues {
  domainId: string
  username: string
  name: string
  email: string
  password: string
  status: Extract<UserStatus, "active" | "suspended">
}

function defaultsFor(user: AdminUser | null): UserFormValues {
  return {
    domainId: user?.domain_id ?? "",
    username: user?.username ?? "",
    name: user?.name ?? "",
    email: user?.email ?? "",
    password: "",
    status: user?.status === "suspended" ? "suspended" : "active",
  }
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export interface UserFormDrawerProps {
  open: boolean
  user: AdminUser | null
  onClose: () => void
  onCreate: (input: CreateUserRequest) => Promise<unknown>
  onUpdate: (id: string, input: UpdateUserRequest) => Promise<unknown>
}

export const UserFormDrawer = ({
  open,
  user,
  onClose,
  onCreate,
  onUpdate,
}: UserFormDrawerProps) => {
  const isEdit = user !== null
  const { control, handleSubmit, reset, setError, formState } = useForm<UserFormValues>({
    mode: "onBlur",
    reValidateMode: "onChange",
    defaultValues: defaultsFor(user),
  })

  // Dominios para el selector de alta (el backend ya limita el alcance del maestro).
  const domainsQuery = useQuery({
    queryKey: ["admin", "domains", "picker"],
    queryFn: () => api.get<DomainPage>("/admin/domains", { query: { limit: 100 } }),
    enabled: open && !isEdit,
  })

  useEffect(() => {
    if (open) reset(defaultsFor(user))
  }, [open, user, reset])

  const formError = formState.errors.root?.message ?? null
  const setFormError = (message: string) => setError("root", { message })

  const onSubmit = handleSubmit(async (values) => {
    try {
      if (isEdit) {
        const update: UpdateUserRequest = {
          username: values.username.trim().toLowerCase(),
          name: values.name.trim() || null,
          email: values.email.trim() ? values.email.trim() : null,
          status: values.status,
        }
        await onUpdate(user.id, update)
      } else {
        const create: CreateUserRequest = {
          domain_id: values.domainId,
          username: values.username.trim().toLowerCase(),
          ...(values.password ? { password: values.password } : {}),
          ...(values.name.trim() ? { name: values.name.trim() } : {}),
          ...(values.email.trim() ? { email: values.email.trim() } : {}),
        }
        await onCreate(create)
      }
      onClose()
    } catch (error) {
      if (error instanceof ApiError) {
        const mapped = applyFieldErrors(remap(error.problem), setError)
        if (!mapped) setFormError(problemMessage(error.problem))
      } else {
        throw error
      }
    }
  })

  const domains = domainsQuery.data?.data ?? []

  return (
    <AppDrawerForm
      open={open}
      title={isEdit ? i18n.editDrawer.title : i18n.createDrawer.title}
      submitLabel={isEdit ? i18n.editDrawer.submit : i18n.createDrawer.submit}
      submitting={formState.isSubmitting}
      error={formError}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      {!isEdit ? (
        <FormField
          control={control}
          name="domainId"
          label={i18n.createDrawer.domain}
          required
          hint={i18n.createDrawer.domainHint}
          rules={{ required: i18n.validation.domainRequired }}
        >
          {(field, invalid) => (
            <Dropdown
              aria-invalid={invalid}
              selectedOptions={field.value ? [field.value] : []}
              value={domains.find((d) => d.id === field.value)?.slug ?? ""}
              onOptionSelect={(_, data) => field.onChange(data.optionValue ?? "")}
            >
              {domains.map((d) => (
                <Option key={d.id} value={d.id}>
                  {d.slug}
                </Option>
              ))}
            </Dropdown>
          )}
        </FormField>
      ) : null}

      <FormField
        control={control}
        name="username"
        label={i18n.fields.username}
        required
        hint={i18n.fields.usernameHint}
        rules={{
          required: i18n.validation.usernameRequired,
          validate: (value) =>
            USERNAME_REGEX.test(String(value).trim().toLowerCase()) ||
            i18n.validation.usernameFormat,
        }}
      >
        {(field, invalid) => <Input {...field} aria-invalid={invalid} />}
      </FormField>

      <FormField control={control} name="name" label={i18n.fields.name}>
        {(field) => <Input {...field} />}
      </FormField>

      <FormField
        control={control}
        name="email"
        label={i18n.fields.email}
        rules={{
          validate: (value) =>
            String(value).trim() === "" ||
            EMAIL_RE.test(String(value).trim()) ||
            i18n.validation.emailFormat,
        }}
      >
        {(field, invalid) => <Input {...field} type="email" aria-invalid={invalid} />}
      </FormField>

      {!isEdit ? (
        <FormField
          control={control}
          name="password"
          label={i18n.fields.password}
          hint={i18n.createDrawer.passwordHint}
        >
          {(field) => <Input {...field} type="password" autoComplete="new-password" />}
        </FormField>
      ) : (
        <FormField control={control} name="status" label={i18n.fields.status}>
          {(field) => (
            <Dropdown
              selectedOptions={[field.value]}
              value={field.value === "suspended" ? i18n.status.suspended : i18n.status.active}
              onOptionSelect={(_, data) =>
                field.onChange((data.optionValue as UserFormValues["status"]) ?? "active")
              }
            >
              <Option value="active">{i18n.status.active}</Option>
              <Option value="suspended">{i18n.status.suspended}</Option>
            </Dropdown>
          )}
        </FormField>
      )}
    </AppDrawerForm>
  )
}

// El backend marca campos por su nombre de contrato; coinciden con los del formulario.
function remap(problem: ProblemDetails): ProblemDetails {
  if (!problem.errors) return problem
  const map: Record<string, string> = { domain_id: "domainId" }
  return {
    ...problem,
    errors: problem.errors.map((e) => ({ ...e, field: map[e.field] ?? e.field })),
  }
}
